import { ArrowLeftIcon } from "@heroicons/react/outline";
import React, { useEffect, useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { USD_CURRENCY_LOCALE_STRING_CONFIG } from "../../../components/CurrencySelector";
import Molecule from "../../../components/Molecule";
import { useBillingPlans } from "../../../context/billingPlans";
import { FilteredListProvider } from "../../../context/filteredList";
import { useMembers } from "../../../context/members";
import { useWorkspaces } from "../../../context/workspaces";
import AppShell from "../../../layout/AppShell";
import { PaymentStep, PlanType } from "../../../utils/types";
import BaseSubscriptionPlan from "./BaseSubscriptionPlan";
import PaymentReview from "./PaymentReview";
import PrepaidVolumeDiscounts from "./PrepaidVolumeDiscounts";

interface Props extends RouteComponentProps {}

const PlansCheckout: React.FC<Props> = (props) => {
  const { newPlan, setPaymentStep, currentPlan } = useBillingPlans();
  const { currentWorkspace } = useWorkspaces();
  const history = useHistory();
  const { isCurrentUserAdmin } = useMembers();

  const checkoutContainer = useMemo(
    () => document.getElementById("checkout-container"),
    []
  );

  useEffect(() => {
    if (
      currentWorkspace?.active_plan?.type === PlanType.pro ||
      currentWorkspace?.active_plan?.type === PlanType.business
    ) {
      return history.push("/pricing");
    }

    checkoutContainer?.scrollTo({ top: 0, behavior: "smooth" });
  }, [
    checkoutContainer,
    currentWorkspace?.active_plan?.type,
    history,
    isCurrentUserAdmin
  ]);

  return (
    <FilteredListProvider>
      <AppShell
        {...props}
        className="h-screen"
        currentPage={
          <div className="flex items-center">
            <span>Checkout</span>
          </div>
        }
      >
        <Molecule className="fixed right-0" />
        <div
          id="checkout-container"
          className="flex flex-col w-full items-center overflow-y-auto overflow-x-hidden pb-10 z-10"
        >
          <div className="flex flex-col w-full max-w-7xl mx-auto px-4">
            <div className="mb-2">
              <div
                onClick={() => {
                  setPaymentStep(PaymentStep.plans);
                  history.push("/pricing");
                }}
                className="flex mr-auto items-center sm:mt-6 cursor-pointer"
              >
                <ArrowLeftIcon className="w-3 mr-2" />
                <span className="text-sm font-bold">Return to pricing</span>
              </div>
            </div>
            <div className="flex w-full flex-col">
              <p className="text-xl sm:text-5xl font-bold sm:font-extrabold dark:text-white mt-4">
                Checkout
              </p>

              <div className="flex w-full flex-col lg:flex-row">
                <div className="flex flex-col w-full lg:w-8/12 mr-4">
                  <BaseSubscriptionPlan />

                  <PrepaidVolumeDiscounts />

                  <PaymentReview />
                </div>
                <div className="hidden md:flex flex-col w-full lg:w-4/12 lg:ml-4 mt-5 lg:mt-8">
                  <div className="flex flex-col items-center rounded-lg p-3 sm:p-5 border shadow-md bg-white dark:bg-gray-800 dark:border-gray-500">
                    <div className="flex w-full flex-col divide-y space-y-4 dark:divide-gray-500">
                      <div className="flex w-full items-center justify-between pt-4">
                        <div className="flex flex-col">
                          <span className="text-xs sm:text-sm mb-1 font-medium dark:text-gray-200">
                            Feature plan
                          </span>
                          <span className="sm:text-lg font-bold dark:text-gray-200">
                            {newPlan.plan?.name}
                          </span>
                        </div>

                        <div className="flex items-center">
                          <span className="text-lg font-bold">
                            {Number(
                              newPlan.plan?.type === currentPlan?.type
                                ? "0.00"
                                : `${(newPlan.plan?.price || 0) / 100}`
                            ).toLocaleString(
                              "en-US",
                              USD_CURRENCY_LOCALE_STRING_CONFIG
                            )}
                          </span>
                          <span className="text-sm dark:text-gray-200 ml-1">
                            /month
                          </span>
                        </div>
                      </div>

                      <div className="flex w-full items-center justify-between pt-4">
                        <div className="flex flex-col">
                          <span className="text-xs sm:text-sm mb-1 font-medium dark:text-gray-200">
                            Hotspots
                          </span>
                          <span className="sm:text-lg font-bold dark:text-gray-200">
                            {newPlan.hotspotAmount.toLocaleString()}
                          </span>
                        </div>

                        <div className="flex items-center">
                          <span className="text-lg font-bold">
                            {newPlan.hotspotAmountPrice.toLocaleString(
                              "en-US",
                              USD_CURRENCY_LOCALE_STRING_CONFIG
                            )}
                          </span>
                          <span className="text-sm dark:text-gray-200 ml-1">
                            /month
                          </span>
                        </div>
                      </div>

                      <div className="flex w-full items-center justify-between pt-4">
                        <div className="flex flex-col">
                          <span className="text-xs sm:text-sm mb-1 font-medium dark:text-gray-200">
                            Super simulations/month
                          </span>
                          <span className="sm:text-lg font-bold dark:text-gray-200">
                            {newPlan.simulationAmount.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-lg font-bold">
                            {newPlan.simulationAmountPrice.toLocaleString(
                              "en-US",
                              USD_CURRENCY_LOCALE_STRING_CONFIG
                            )}
                          </span>
                          <span className="text-sm dark:text-gray-200 ml-1">
                            /month
                          </span>
                        </div>
                      </div>

                      <div className="flex w-full items-center justify-between pt-4">
                        <div className="flex flex-col">
                          <span className="text-xs sm:text-sm mb-1 font-medium dark:text-gray-200">
                            Locations
                          </span>

                          <span className="sm:text-lg font-bold dark:text-gray-200">
                            {newPlan.hotspotAmount * 2 +
                              newPlan.simulationAmount * 2}
                          </span>
                        </div>

                        <div className="flex items-center">
                          <span className="text-lg font-bold">$0.00</span>
                          <span className="text-sm dark:text-gray-200 ml-1">
                            /month
                          </span>
                        </div>
                      </div>

                      <div className="flex w-full items-center justify-between pt-4">
                        <span className="text-3xl mb-1 font-medium dark:text-gray-200">
                          Total
                        </span>
                        <div>
                          <div className="flex items-center">
                            <span className="text-3xl font-bold">
                              {Number(newPlan.total).toLocaleString(
                                "en-US",
                                USD_CURRENCY_LOCALE_STRING_CONFIG
                              )}
                            </span>
                            <span className="text-sm dark:text-gray-200 ml-1">
                              /month
                            </span>
                          </div>
                          <div className="text-xs">+ VAT if applicable</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppShell>
    </FilteredListProvider>
  );
};

export default PlansCheckout;
